import type {
  CoachRuntimeServiceReadModel,
  CoachServiceCorrectionNotice,
  CoachServiceCorrectionOperatorPreviewReadModel,
  CoachServiceCorrectionOperatorResult,
  ExecuteCoachServiceCorrectionCommand,
  PreviewCoachServiceCorrectionRequest,
  UpdateCoachServiceCorrectionNoticeRequest,
} from "@billiard/contracts";
import {
  isCoachServiceCorrectionOperatorPreviewReadModel,
  isCoachServiceCorrectionOperatorResult,
  isExecuteCoachServiceCorrectionCommand,
  isPreviewCoachServiceCorrectionRequest,
  isUpdateCoachServiceCorrectionNoticeRequest,
} from "@billiard/contracts";
import { ApiRequestError, requestOperatorJson } from "./api";

const stableId = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u;

export interface CoachServiceCorrectionContext {
  tenantId: string;
  storeId: string;
  actorId: string;
}

export interface CoachServiceCorrectionCommand {
  context: CoachServiceCorrectionContext;
  preview: CoachServiceCorrectionOperatorPreviewReadModel;
  request: ExecuteCoachServiceCorrectionCommand;
}

function contextIsValid(context: CoachServiceCorrectionContext): boolean {
  return (
    stableId.test(context.tenantId) &&
    stableId.test(context.storeId) &&
    stableId.test(context.actorId)
  );
}

function exactJsonEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function noticeIsValid(value: unknown): value is CoachServiceCorrectionNotice {
  if (!isRecord(value)) return false;
  const keys = [
    "id",
    "correctionId",
    "serviceId",
    "coachId",
    "kind",
    "title",
    "message",
    "status",
    "version",
    "createdAt",
    "deliveredAt",
    "readAt",
  ];
  const canonicalDate = (item: unknown): item is string => {
    if (typeof item !== "string") return false;
    const parsed = Date.parse(item);
    return Number.isFinite(parsed) && new Date(parsed).toISOString() === item;
  };
  const dateOrNull = (item: unknown): boolean => item === null || canonicalDate(item);
  return (
    Object.keys(value).length === keys.length &&
    keys.every((key) => Object.hasOwn(value, key)) &&
    typeof value.id === "string" &&
    stableId.test(value.id) &&
    typeof value.correctionId === "string" &&
    stableId.test(value.correctionId) &&
    typeof value.serviceId === "string" &&
    stableId.test(value.serviceId) &&
    typeof value.coachId === "string" &&
    stableId.test(value.coachId) &&
    ["MISASSIGN_CANCEL", "LATE_START_ADJUST"].includes(String(value.kind)) &&
    typeof value.title === "string" &&
    value.title.length >= 2 &&
    value.title.length <= 80 &&
    typeof value.message === "string" &&
    value.message.length >= 2 &&
    value.message.length <= 500 &&
    ["PENDING", "DELIVERED", "READ"].includes(String(value.status)) &&
    typeof value.version === "number" &&
    Number.isSafeInteger(value.version) &&
    value.version >= 1 &&
    canonicalDate(value.createdAt) &&
    dateOrNull(value.deliveredAt) &&
    dateOrNull(value.readAt) &&
    ((value.status === "PENDING" && value.deliveredAt === null && value.readAt === null) ||
      (value.status === "DELIVERED" && value.deliveredAt !== null && value.readAt === null) ||
      (value.status === "READ" && value.deliveredAt !== null && value.readAt !== null))
  );
}

export function coachServiceCorrectionCommandIsValid(
  command: CoachServiceCorrectionCommand,
): boolean {
  const { context, preview, request } = command;
  return (
    contextIsValid(context) &&
    isCoachServiceCorrectionOperatorPreviewReadModel(preview) &&
    preview.canExecute &&
    isExecuteCoachServiceCorrectionCommand(request) &&
    request.kind === preview.kind &&
    request.correctedStartedAt === preview.correctedStartedAt &&
    request.expectedServiceVersion === preview.expectedServiceVersion &&
    request.reason === preview.reason &&
    request.correctionToken === preview.correctionToken &&
    request.previewGeneratedAt === preview.generatedAt
  );
}

function resultMatchesCommand(
  result: CoachServiceCorrectionOperatorResult,
  command: CoachServiceCorrectionCommand,
): boolean {
  const { preview, request, context } = command;
  const corrected = result.correctedService;
  return (
    result.kind === request.kind &&
    result.serviceId === preview.serviceId &&
    result.reason === request.reason &&
    result.idempotencyKey === request.idempotencyKey &&
    result.actorId === context.actorId &&
    exactJsonEqual(result.before, preview.before) &&
    exactJsonEqual(result.after, preview.after) &&
    corrected.id === preview.serviceId &&
    corrected.coachId === preview.coachId &&
    corrected.tableNumber === preview.tableNumber &&
    corrected.version === preview.expectedServiceVersion + 1 &&
    corrected.startedAt ===
      (request.kind === "LATE_START_ADJUST"
        ? request.correctedStartedAt
        : preview.originalStartedAt) &&
    corrected.billedMinutes === 0 &&
    corrected.effectiveSeconds === 0 &&
    corrected.accruedServiceFeeFen === 0 &&
    (request.kind === "MISASSIGN_CANCEL"
      ? corrected.status === "ABNORMAL_TERMINATED"
      : corrected.status === "ACTIVE") &&
    result.notice.coachId === preview.coachId &&
    result.notice.serviceId === preview.serviceId &&
    result.notice.kind === preview.kind
  );
}

function parsePreview(
  value: unknown,
  serviceId: string,
  request: PreviewCoachServiceCorrectionRequest,
): CoachServiceCorrectionOperatorPreviewReadModel {
  if (
    !isCoachServiceCorrectionOperatorPreviewReadModel(value) ||
    value.serviceId !== serviceId ||
    value.kind !== request.kind ||
    value.correctedStartedAt !== request.correctedStartedAt ||
    value.reason !== request.reason
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "助教服务纠正预览无法与所选服务、时间和原因逐项对应",
    );
  }
  return value;
}

function parseResult(
  value: unknown,
  command: CoachServiceCorrectionCommand,
): CoachServiceCorrectionOperatorResult {
  if (
    !coachServiceCorrectionCommandIsValid(command) ||
    !isCoachServiceCorrectionOperatorResult(value) ||
    !resultMatchesCommand(value, command)
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "助教服务纠正结果无法与确认前的助教、球桌、时间、费用和提成逐项对应，页面继续锁定",
    );
  }
  return value;
}

export async function previewCoachServiceCorrection(
  context: CoachServiceCorrectionContext,
  service: CoachRuntimeServiceReadModel,
  request: PreviewCoachServiceCorrectionRequest,
  signal?: AbortSignal,
): Promise<CoachServiceCorrectionOperatorPreviewReadModel> {
  if (
    !contextIsValid(context) ||
    !["ASSIGNED", "ACTIVE"].includes(service.status) ||
    !isPreviewCoachServiceCorrectionRequest(request) ||
    (request.kind === "LATE_START_ADJUST" && service.status !== "ACTIVE")
  ) {
    throw new ApiRequestError(
      "REQUEST_VALIDATION_FAILED",
      "所选服务、纠正类型、时间或原因不完整，系统没有生成预览",
      400,
    );
  }
  const value = await requestOperatorJson<unknown>(
    `/v1/coaches/services/${encodeURIComponent(service.id)}/corrections/preview`,
    {
      method: "POST",
      cache: "no-store",
      body: JSON.stringify(request),
      ...(signal ? { signal } : {}),
    },
  );
  const preview = parsePreview(value, service.id, request);
  if (
    preview.expectedServiceVersion !== service.version ||
    preview.coachId !== service.coachId ||
    preview.tableNumber !== service.tableNumber ||
    preview.serviceTypeName !== service.serviceTypeName ||
    preview.serviceStatus !== service.status ||
    preview.assignedAt !== service.assignedAt ||
    preview.originalStartedAt !== service.startedAt
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "助教服务纠正预览与页面当前显示的服务不一致，请刷新后重新确认",
    );
  }
  return preview;
}

export async function executeCoachServiceCorrection(
  command: CoachServiceCorrectionCommand,
): Promise<CoachServiceCorrectionOperatorResult> {
  if (!coachServiceCorrectionCommandIsValid(command)) {
    throw new ApiRequestError(
      "REQUEST_VALIDATION_FAILED",
      "纠正确认内容不完整或预览已经变化，系统没有执行",
      400,
    );
  }
  const value = await requestOperatorJson<unknown>(
    `/v1/coaches/services/${encodeURIComponent(command.preview.serviceId)}/corrections`,
    { method: "POST", cache: "no-store", body: JSON.stringify(command.request) },
  );
  return parseResult(value, command);
}

export async function getCoachServiceCorrectionByRequest(
  command: CoachServiceCorrectionCommand,
  signal?: AbortSignal,
): Promise<CoachServiceCorrectionOperatorResult | null> {
  if (!coachServiceCorrectionCommandIsValid(command)) {
    throw new ApiRequestError(
      "REQUEST_VALIDATION_FAILED",
      "本机保存的助教纠正原请求不完整，系统没有查询或重试",
      400,
    );
  }
  try {
    const value = await requestOperatorJson<unknown>(
      `/v1/coaches/services/${encodeURIComponent(command.preview.serviceId)}/corrections/by-request/${encodeURIComponent(command.request.idempotencyKey)}`,
      { cache: "no-store", ...(signal ? { signal } : {}) },
    );
    return parseResult(value, command);
  } catch (error) {
    if (
      error instanceof ApiRequestError &&
      error.statusCode === 404 &&
      error.code === "COACH_SERVICE_CORRECTION_RESULT_NOT_FOUND"
    ) {
      return null;
    }
    throw error;
  }
}

function parseNotice(value: unknown, expectedCoachId: string): CoachServiceCorrectionNotice {
  if (!noticeIsValid(value) || value.coachId !== expectedCoachId) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "助教纠正通知无法确认属于当前助教，页面已停止显示",
    );
  }
  return value;
}

export async function listMyCoachServiceCorrectionNotices(
  context: CoachServiceCorrectionContext,
  expectedCoachId: string,
  signal?: AbortSignal,
): Promise<CoachServiceCorrectionNotice[]> {
  if (!contextIsValid(context) || !stableId.test(expectedCoachId)) {
    throw new ApiRequestError("REQUEST_VALIDATION_FAILED", "当前助教身份无法安全确认", 400);
  }
  const value = await requestOperatorJson<unknown>("/v1/coaches/service-correction-notices", {
    cache: "no-store",
    ...(signal ? { signal } : {}),
  });
  if (!Array.isArray(value) || value.length > 200) {
    throw new ApiRequestError("GATEWAY_RESPONSE_INVALID", "助教纠正通知列表无法安全确认");
  }
  return value.map((notice) => parseNotice(notice, expectedCoachId));
}

export async function updateMyCoachServiceCorrectionNotice(
  context: CoachServiceCorrectionContext,
  expectedCoachId: string,
  baseline: CoachServiceCorrectionNotice,
  request: UpdateCoachServiceCorrectionNoticeRequest,
): Promise<CoachServiceCorrectionNotice> {
  if (
    !contextIsValid(context) ||
    baseline.coachId !== expectedCoachId ||
    !noticeIsValid(baseline) ||
    !isUpdateCoachServiceCorrectionNoticeRequest(request) ||
    request.expectedVersion !== baseline.version ||
    (request.status === "DELIVERED" && baseline.status !== "PENDING") ||
    (request.status === "READ" && baseline.status !== "DELIVERED")
  ) {
    throw new ApiRequestError(
      "REQUEST_VALIDATION_FAILED",
      "通知状态已经变化或不属于当前助教，请刷新后重试",
      400,
    );
  }
  const value = await requestOperatorJson<unknown>(
    `/v1/coaches/service-correction-notices/${encodeURIComponent(baseline.id)}/delivery`,
    { method: "POST", cache: "no-store", body: JSON.stringify(request) },
  );
  const notice = parseNotice(value, expectedCoachId);
  if (
    notice.id !== baseline.id ||
    notice.correctionId !== baseline.correctionId ||
    notice.serviceId !== baseline.serviceId ||
    notice.kind !== baseline.kind ||
    notice.title !== baseline.title ||
    notice.message !== baseline.message ||
    notice.createdAt !== baseline.createdAt ||
    notice.status !== request.status ||
    notice.version !== baseline.version + 1 ||
    (request.status === "DELIVERED" && (notice.deliveredAt === null || notice.readAt !== null)) ||
    (request.status === "READ" &&
      (notice.deliveredAt !== baseline.deliveredAt || notice.readAt === null))
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "通知更新结果无法与当前通知对应，请刷新后核对",
    );
  }
  return notice;
}

export function coachServiceCorrectionOutcomeIsUnknown(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return true;
  return (
    error.code === "GATEWAY_UNREACHABLE" ||
    error.code === "GATEWAY_RESPONSE_INVALID" ||
    error.code === "OPERATOR_IDENTITY_CHANGED" ||
    (error.statusCode !== undefined && error.statusCode >= 500)
  );
}

export function coachServiceCorrectionFailureMessage(error: unknown): string {
  if (!(error instanceof ApiRequestError)) {
    return "门店连接中断，纠正结果尚未确认；系统会先查询原请求，请勿再次提交。";
  }
  const messages: Record<string, string> = {
    COACH_SERVICE_CORRECTION_REQUEST_INVALID:
      "纠正内容不完整，系统没有执行。请重新选择类型、时间并填写原因。",
    COACH_SERVICE_CORRECTION_NOT_ALLOWED:
      "当前服务状态不允许这样纠正。请刷新后选择仍待上钟或服务中的记录。",
    COACH_SERVICE_CORRECTION_PREVIEW_REQUIRED: "当前预览已失效。请重新生成预览并核对金额后再确认。",
    COACH_SERVICE_CORRECTION_PREVIEW_EXPIRED:
      "费用和提成预览已超过 5 分钟，系统没有执行。输入内容已经保留，请重新生成预览并再次确认。",
    COACH_SERVICE_CORRECTION_TOKEN_MISMATCH:
      "服务或计费规则已经变化。请刷新并重新生成预览，不能沿用旧确认。",
    COACH_SERVICE_VERSION_CONFLICT: "助教服务已经被其他员工更新。请刷新后重新核对。",
    REQUEST_KEY_OWNED_BY_OTHER_ACTOR:
      "原请求属于另一名员工，当前账号不能接管。请切回原账号核对结果。",
    IDEMPOTENCY_COLLISION: "原请求号对应的内容不同，系统没有执行。请刷新后重新操作。",
    AUTH_REQUIRED: "登录状态已失效，系统没有执行。请重新登录后再操作。",
    AUTHORIZATION_DEFAULT_DENY: "当前账号没有执行助教纠正的权限。",
    CONDITIONAL_MODULE_DISABLED: "门店尚未启用助教模块，系统没有执行。",
  };
  return messages[error.code] ?? error.message;
}
